import React, { memo, useMemo, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { VariableSizeGrid as Grid, GridChildComponentProps, areEqual } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'
import { Box, Flex } from 'rebass'
import _ from 'lodash'

import {
    REVIEW_MODAL_TYPE,
    selectSelectedCampaignSummary,
    setRejectPopupSubmissionUuid,
    setReviewModalOpen,
    setReviewModalType,
} from 'state/review'
import { BatchSubmissionState, SUBMISSION_STATUS_ENUM } from 'state/submissions'
import { CAMPAIGN_STATUS_ENUM } from 'state/campaigns'
import { selectTasks } from 'state/tasks'
import { SubmissionContainer } from './SubmissionList'
import PartiposterProfile from '../PartiposterProfile'
import { SimpleButtonX } from 'components/ButtonFe'
import BatchProgressBlock from './BatchProgressBlock'
import { theme } from 'theme'
import InWaitingListTooltip from './BatchProgressBlock/InWaitingListToolTip'
import { selectCanDirectReview } from 'state/profile'
import { SubmissionsWrapper } from './styles'

interface ReviewAllTasksProps {
    handleRejectTagChange: any
    submissions: BatchSubmissionState[]
}

const Cell: React.FC<GridChildComponentProps> = memo(({ columnIndex, rowIndex, style, data }) => {
    const dispatch = useDispatch()
    const { columnCount, submissions, setRowHeight, isRowColumnMaxHeight, showFbAccount, campaignStatus } = data
    const index = columnIndex + rowIndex * columnCount
    const submission = index < submissions.length ? submissions[index] : {}
    const rowRef = useRef<any>({})
    const isSubmissionPaid = submission ? submission.status === SUBMISSION_STATUS_ENUM.PAID : false
    const isCampaignLive = campaignStatus ? campaignStatus === CAMPAIGN_STATUS_ENUM.LIVE : false
    const canDirectReview = useSelector(selectCanDirectReview)

    const handleReject = () => {
        if (submission) {
            dispatch(setRejectPopupSubmissionUuid(submission.uuid))
            dispatch(setReviewModalType(REVIEW_MODAL_TYPE.BATCH_SINGLE_REJECT))
            dispatch(setReviewModalOpen(true))
        }
    }

    useEffect(() => {
        if (rowRef.current) {
            setRowHeight(rowIndex, columnIndex, rowRef.current.clientHeight)
        }
    }, [rowRef, rowRef.current && rowRef.current.clientHeight])

    return (
        <Box style={style} key={columnIndex + '_' + rowIndex}>
            {!_.isEmpty(submission) ? (
                <SubmissionContainer
                    flexDirection="column"
                    height="-webkit-fill-available"
                    m={theme.spacing.xxxs}
                    isBulkActionErr={false}
                    sx={{
                        textAlign: 'center',
                    }}
                >
                    <Box
                        ref={rowRef}
                        py={theme.spacing.xxs}
                        width="100%"
                        {...(!isRowColumnMaxHeight(rowIndex, columnIndex) && {
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                        })}
                    >
                        <Box px={theme.spacing.xxs}>
                            <PartiposterProfile
                                submission={submission}
                                submissionUuid={submission.uuid}
                                options={{ instagram: true, facebook: showFbAccount, joinedAt: true }}
                                TopRightComponent={
                                    <>
                                        {submission.is_inside_batch != null && !submission.is_inside_batch && (
                                            <InWaitingListTooltip />
                                        )}
                                        {canDirectReview && isCampaignLive && !isSubmissionPaid ? (
                                            <Box
                                                sx={{
                                                    borderWidth: '0.15rem',
                                                    borderStyle: 'solid',
                                                    borderColor: theme.colors.frenchRose,
                                                    borderRadius: '2px',
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                }}
                                                width={22}
                                                height={22}
                                            >
                                                <SimpleButtonX
                                                    width="14px"
                                                    height="14px"
                                                    stroke={theme.colors.frenchRose}
                                                    strokeWidth={2.5}
                                                    onClick={handleReject}
                                                />
                                            </Box>
                                        ) : null}
                                    </>
                                }
                            />
                        </Box>
                        <Flex flexDirection="column" flex="1">
                            <BatchProgressBlock batchSubmission={submission} />
                        </Flex>
                    </Box>
                </SubmissionContainer>
            ) : (
                <></>
            )}
        </Box>
    )
}, areEqual)
Cell.displayName = 'Cell'

const ReviewAllTasks: React.FC<ReviewAllTasksProps> = ({ handleRejectTagChange, submissions }) => {
    const campaignSummary = useSelector(selectSelectedCampaignSummary)
    const tasks = useSelector(selectTasks)
    const showFbAccount = useMemo(
        () =>
            tasks.some(task => {
                return task.platform == 2
            }),
        tasks,
    )
    const campaignStatus = campaignSummary ? campaignSummary.status : null

    const rowHeights = useRef<any>({})
    const gridRef = useRef<any>({})
    const maxHeightRowColumns = useRef<any>({})

    if (!campaignSummary) {
        return null
    }

    const columnCount = campaignSummary.tasks.length > 2 ? 1 : 6 - 2 * campaignSummary.tasks.length

    const getRowHeight = (index: number): number => {
        return rowHeights.current[index] || 500
    }
    const setRowHeight = (rowIndex: number, columnIndex: number, size: number): void => {
        const currentSize = rowHeights.current[rowIndex] ? rowHeights.current[rowIndex] : 0
        if (size >= currentSize + 15) {
            gridRef.current.resetAfterRowIndex(0)
            if (maxHeightRowColumns.current[rowIndex]) {
                Object.keys(maxHeightRowColumns.current[rowIndex]).forEach(key => {
                    maxHeightRowColumns.current[rowIndex][key] = false
                })
            }
            maxHeightRowColumns.current[rowIndex] = { ...maxHeightRowColumns.current[rowIndex], [columnIndex]: true }
            rowHeights.current = { ...rowHeights.current, [rowIndex]: Math.max(currentSize, size) }
        }
    }

    const isRowColumnMaxHeight = (rowIndex: number, columnIndex: number): boolean => {
        try {
            return maxHeightRowColumns.current[rowIndex][columnIndex]
        } catch {
            return true
        }
    }

    return (
        <SubmissionsWrapper>
            <AutoSizer>
                {({ height, width }) => {
                    return (
                        <Grid
                            ref={gridRef}
                            columnCount={columnCount}
                            rowCount={Math.ceil(submissions.length / columnCount)}
                            height={height}
                            width={width}
                            columnWidth={(): number =>
                                campaignSummary.tasks.length === 1
                                    ? (width + 90) / columnCount
                                    : (width - 30) / columnCount
                            }
                            rowHeight={index => getRowHeight(index) + 15}
                            itemData={{
                                columnCount,
                                submissions,
                                setRowHeight,
                                isRowColumnMaxHeight,
                                handleRejectTagChange,
                                showFbAccount,
                                campaignStatus,
                            }}
                        >
                            {Cell}
                        </Grid>
                    )
                }}
            </AutoSizer>
        </SubmissionsWrapper>
    )
}

export default ReviewAllTasks
